import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchBoundary, fetchNetwork } from './api/participation-api';
import { TechnicalAccessModal } from './components/TechnicalAccessModal';
import { HeroCards, type CardScale } from './components/HeroCards';
import { ContributionModal } from './components/ContributionModal';
import { MapSection } from './components/MapSection';
import { InfoModal } from './components/InfoModal';
import './participation.css';

const A11Y_KEY = 'wayline_p_a11y';

// ── Neutral institutional header ──────────────────────────────────────────────

interface PageHeaderProps {
  onInfoClick: () => void;
  a11yMode: boolean;
  onA11yToggle: () => void;
  onTechTeamClick: () => void;
}

function PageHeader({ onInfoClick, a11yMode, onA11yToggle, onTechTeamClick }: PageHeaderProps): JSX.Element {
  const bg      = a11yMode ? '#1e293b' : '#fff';
  const border  = a11yMode ? '#334155' : '#ececec';
  const txtMain = a11yMode ? '#f1f5f9' : '#1a1a2e';
  const txtSub  = a11yMode ? '#94a3b8' : '#6b7280';
  const btnBdr  = a11yMode ? '#475569' : '#e0e0e0';
  const btnTxt  = a11yMode ? '#e2e8f0' : '#374151';

  const s: CSSProperties = {
    background: bg,
    borderBottom: `1px solid ${border}`,
    padding: '14px 28px',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    position: 'sticky',
    top: 0,
    zIndex: 500,
    transition: 'background 0.2s, border-color 0.2s',
  };

  function pillStyle(active: boolean): CSSProperties {
    return {
      padding: '7px 16px',
      borderRadius: 20,
      border: `1.5px solid ${active ? (a11yMode ? '#60a5fa' : '#1a1a2e') : btnBdr}`,
      background: active ? (a11yMode ? '#1e3a5f' : '#1a1a2e') : bg,
      color: active ? '#fff' : btnTxt,
      fontSize: a11yMode ? 16 : 12.5,
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      transition: 'border-color 0.15s, background 0.15s',
      whiteSpace: 'nowrap' as const,
    };
  }

  return (
    <header style={s}>
      <div style={{ width: 36, height: 36, borderRadius: 10, background: a11yMode ? '#0f172a' : '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M9 22V12h6v10" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <div>
        <div style={{ fontSize: a11yMode ? 19 : 15, fontWeight: 800, color: txtMain, lineHeight: 1.15 }}>
          Consulta Ciudadana
        </div>
        <div style={{ fontSize: a11yMode ? 14 : 11.5, color: txtSub, lineHeight: 1.2 }}>
          San Cristóbal de las Casas
        </div>
      </div>

      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          type="button"
          onClick={onA11yToggle}
          aria-pressed={a11yMode}
          aria-label={a11yMode ? 'Desactivar modo accesible' : 'Activar modo accesible'}
          style={pillStyle(a11yMode)}
        >
          <span aria-hidden="true">{a11yMode ? '◑' : '◐'}</span>
          {a11yMode ? 'Vista estándar' : 'Modo accesible'}
        </button>

        <button
          type="button"
          onClick={onInfoClick}
          aria-label="Sobre la consulta"
          style={pillStyle(false)}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = a11yMode ? '#60a5fa' : '#1a1a2e'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = btnBdr; }}
        >
          <span aria-hidden="true">ⓘ</span>
          Sobre la consulta
        </button>

        <button
          type="button"
          onClick={onTechTeamClick}
          aria-label="Acceso equipo técnico"
          style={{
            ...pillStyle(false),
            opacity: 0.55,
            fontSize: a11yMode ? 14 : 11.5,
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLButtonElement).style.opacity = '1';
            (e.currentTarget as HTMLButtonElement).style.borderColor = a11yMode ? '#60a5fa' : '#1a1a2e';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLButtonElement).style.opacity = '0.55';
            (e.currentTarget as HTMLButtonElement).style.borderColor = btnBdr;
          }}
        >
          Equipo técnico
        </button>
      </div>
    </header>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function ParticipationPage(): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const slug = projectSlug ?? 'sclc';

  const [showTechModal, setShowTechModal] = useState(false);

  const [activeCard,   setActiveCard]   = useState<CardScale | null>(null);
  const [modalScale,   setModalScale]   = useState<'city' | 'sector' | null>(null);
  const [totalSuccess, setTotalSuccess] = useState(0);
  const [showInfo,     setShowInfo]     = useState(false);
  const [a11yMode,     setA11yMode]     = useState<boolean>(() => localStorage.getItem(A11Y_KEY) === '1');

  const [boundary, setBoundary] = useState<GeoJSON.Feature | null>(null);
  const [network,  setNetwork]  = useState<GeoJSON.FeatureCollection | null>(null);

  const mapSectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    fetchBoundary(slug)
      .then(d => setBoundary(d as unknown as GeoJSON.Feature))
      .catch(() => undefined);
    fetchNetwork(slug)
      .then(d => setNetwork(d as unknown as GeoJSON.FeatureCollection))
      .catch(() => undefined);
  }, [slug]);

  useEffect(() => {
    localStorage.setItem(A11Y_KEY, a11yMode ? '1' : '0');
  }, [a11yMode]);

  // Auto-activate "Calle o lugar" when user scrolls to the map without selecting
  useEffect(() => {
    const el = mapSectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setActiveCard(prev => prev === null ? 'place' : prev);
      },
      { threshold: 0.20 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  function handleSuccess(): void { setTotalSuccess(n => n + 1); }
  function toggleA11y(): void    { setA11yMode(v => !v); }

  // ── Derived colors for sections using inline styles ───────────────────────
  const bg       = a11yMode ? '#0f172a' : '#fafafa';
  const txtMain  = a11yMode ? '#f1f5f9' : '#1a1a2e';
  const txtBody  = a11yMode ? '#cbd5e1' : '#4b5563';
  const txtMuted = a11yMode ? '#64748b' : '#6b7280';
  const divLine  = a11yMode ? '#1e293b' : '#e5e7eb';
  const divTxt   = a11yMode ? '#475569' : '#9ca3af';
  const labelTxt = a11yMode ? '#94a3b8' : '#6b7280';
  const fsBase   = a11yMode ? 20 : 14.5;
  const fsLabel  = a11yMode ? 15 : 11.5;
  const h1Size   = a11yMode ? 40 : 28;

  return (
    <div
      className={a11yMode ? 'participation-a11y' : undefined}
      style={{ minHeight: '100dvh', background: bg, fontFamily: "'Segoe UI', system-ui, sans-serif", color: txtMain, transition: 'background 0.2s, color 0.2s' }}
    >
      <PageHeader
        onInfoClick={() => setShowInfo(true)}
        a11yMode={a11yMode}
        onA11yToggle={toggleA11y}
        onTechTeamClick={() => setShowTechModal(true)}
      />

      {showInfo && <InfoModal onClose={() => setShowInfo(false)} />}
      {showTechModal && <TechnicalAccessModal slug={slug} onClose={() => setShowTechModal(false)} />}

      {/* ── Hero section ─────────────────────────────────────────────────── */}
      <div style={{ background: bg, transition: 'background 0.2s' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 28px 52px', width: '100%' }}>

          <div style={{ marginBottom: 36 }}>
            <p style={{ margin: '0 0 10px', fontSize: fsLabel, fontWeight: 600, color: labelTxt, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Participación ciudadana
            </p>
            <h1 style={{ margin: '0 0 14px', fontSize: h1Size, fontWeight: 800, lineHeight: 1.22, letterSpacing: '-0.02em', color: txtMain }}>
              ¿Sobre qué quieres<br />hacer tu aporte?
            </h1>
            <p style={{ margin: 0, fontSize: fsBase, color: txtBody, lineHeight: 1.7, maxWidth: 480 }}>
              Elige la escala de tu aporte. Tu participación es anónima y contribuye
              al diagnóstico urbano participativo de San Cristóbal de las Casas.
            </p>
          </div>

          <HeroCards
            boundary={boundary}
            network={network}
            activeCard={activeCard}
            a11yMode={a11yMode}
            onCityClick={() => { setActiveCard('city');   setModalScale('city'); }}
            onSectorClick={() => { setActiveCard('sector'); setModalScale('sector'); }}
            onPlaceClick={() => {
              setActiveCard('place');
              mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          />

          {totalSuccess > 0 && (
            <p style={{ margin: '16px 0 0', fontSize: a11yMode ? 15 : 13, color: '#22c55e', fontWeight: 600, textAlign: 'right' }}>
              ✓ {totalSuccess} aporte{totalSuccess > 1 ? 's' : ''} registrado{totalSuccess > 1 ? 's' : ''} en esta sesión
            </p>
          )}

          {/* Divider inviting to scroll to the map */}
          <div style={{ marginTop: 32, display: 'flex', alignItems: 'center', gap: 10, padding: '0 4px' }}>
            <div style={{ flex: 1, height: 1, background: divLine }} />
            <span style={{ fontSize: fsLabel + 1, color: divTxt, fontWeight: 500, whiteSpace: 'nowrap' }}>
              o baja al mapa para aportar sobre una calle o lugar
            </span>
            <div style={{ flex: 1, height: 1, background: divLine }} />
          </div>
        </div>
      </div>

      {/* ── Map section ───────────────────────────────────────────────────── */}
      <MapSection
        slug={slug}
        sectionRef={mapSectionRef}
        onContributionSuccess={handleSuccess}
      />

      {/* ── Modal for city / sector ───────────────────────────────────────── */}
      {modalScale && (
        <ContributionModal
          scale={modalScale}
          slug={slug}
          variant="modal"
          onClose={() => setModalScale(null)}
          onSuccess={() => { handleSuccess(); setModalScale(null); }}
        />
      )}

      <div style={{ textAlign: 'center', padding: '12px 0 20px', color: txtMuted, fontSize: 12, lineHeight: 1.6 }}>
        Esta consulta es parte del diagnóstico urbano participativo de San Cristóbal de las Casas.
        Los aportes son anónimos y se usan únicamente para fines de planificación territorial.
      </div>
    </div>
  );
}
